/**
 * Jumbo Text Web Component
 * Adapted from Shopify Horizon Theme
 *
 * Renders text that dynamically scales to fill its container width.
 * Supports blur, reveal, and fade animations with customizable timing.
 */

class JumboText extends HTMLElement {
  constructor() {
    super();
    this.resizeObserver = null;
    this.intersectionObserver = null;
    this.isVisible = false;
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  init() {
    // Wait for fonts to load before calculating size
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        this.calculateFontSize();
        this.setupObservers();
        this.classList.add('ready');
      });
    } else {
      // Fallback for browsers without font loading API
      setTimeout(() => {
        this.calculateFontSize();
        this.setupObservers();
        this.classList.add('ready');
      }, 100);
    }
  }

  cleanup() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }

  isMobile() {
    return window.innerWidth < 750;
  }

  getMaxFontSize() {
    if (this.isMobile() && this.dataset.maxFontSizeMobile) {
      return parseInt(this.dataset.maxFontSizeMobile, 10) || 100;
    }
    return parseInt(this.dataset.maxFontSize, 10) || 500;
  }

  getMinFontSize() {
    return parseInt(this.dataset.minFontSize, 10) || 24;
  }

  calculateFontSize() {
    const container = this.parentElement;
    if (!container) return;

    const containerWidth = container.offsetWidth;
    if (containerWidth === 0) return;

    // Get the text content
    const text = this.textContent.trim();
    if (!text) return;

    // Get computed styles
    const computedStyle = getComputedStyle(this);
    const fontFamily = computedStyle.getPropertyValue('--font-family') || computedStyle.fontFamily || 'inherit';
    const fontWeight = computedStyle.getPropertyValue('--font-weight') || computedStyle.fontWeight || 'inherit';
    const letterSpacing = computedStyle.getPropertyValue('--letter-spacing') || 'normal';
    const textTransform = computedStyle.getPropertyValue('--text-transform') || 'none';

    // Create a temporary span to measure text width
    const measureSpan = document.createElement('span');
    measureSpan.style.cssText = `
      position: absolute;
      visibility: hidden;
      white-space: pre;
      font-family: ${fontFamily};
      font-weight: ${fontWeight};
      letter-spacing: ${letterSpacing};
      text-transform: ${textTransform};
    `;

    // Apply text transform for measurement
    let measureText = text;
    if (textTransform === 'uppercase') {
      measureText = text.toUpperCase();
    } else if (textTransform === 'lowercase') {
      measureText = text.toLowerCase();
    } else if (textTransform === 'capitalize') {
      measureText = text.replace(/\b\w/g, char => char.toUpperCase());
    }

    measureSpan.textContent = measureText;
    document.body.appendChild(measureSpan);

    // Start with a base font size and scale up
    let baseFontSize = 100;
    measureSpan.style.fontSize = `${baseFontSize}px`;
    const baseWidth = measureSpan.offsetWidth;

    let fontSize = baseFontSize;
    if (baseWidth > 0) {
      // Calculate the ratio needed to fill the container
      const ratio = containerWidth / baseWidth;
      fontSize = Math.floor(baseFontSize * ratio * 0.95); // 0.95 to leave small margin
    }

    document.body.removeChild(measureSpan);

    // Apply min/max constraints
    const maxFontSize = this.getMaxFontSize();
    const minFontSize = this.getMinFontSize();

    fontSize = Math.max(minFontSize, Math.min(fontSize, maxFontSize));

    // Apply the calculated font size
    this.style.fontSize = `${fontSize}px`;
  }

  setupObservers() {
    // Resize observer to recalculate on container resize
    this.resizeObserver = new ResizeObserver(() => {
      this.calculateFontSize();
    });

    const container = this.parentElement;
    if (container) {
      this.resizeObserver.observe(container);
    }

    // Intersection observer for animation triggers
    const textEffect = this.dataset.textEffect;
    if (textEffect && textEffect !== 'none') {
      this.setupAnimationObserver();
    } else {
      // No animation, just show immediately
      this.classList.add('jumbo-text-visible');
    }
  }

  setupAnimationObserver() {
    const animationRepeat = this.dataset.animationRepeat === 'true';

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.classList.add('jumbo-text-visible');
            this.isVisible = true;

            if (!animationRepeat) {
              this.intersectionObserver.disconnect();
            }
          } else if (animationRepeat && this.isVisible) {
            this.classList.remove('jumbo-text-visible');
            this.isVisible = false;
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: '0px 0px -10% 0px',
      }
    );

    this.intersectionObserver.observe(this);
  }
}

// Register the custom element
if (!customElements.get('jumbo-text')) {
  customElements.define('jumbo-text', JumboText);
}
